import { DependencyType, ProjectDependencyType, DependencyTypes } from '@common/types/dependencyTypes';
import { ColumnType, Stack, theme, Tooltip } from '@prophecy/ui';
import { caseInsensitiveInclude } from '@prophecy/utils/string';
import React from 'react';

import { EntityIconType } from '../../Entity/constants';
import { EntityIcon } from '../../Entity/EntityIcons';
import { Entity } from '../../types/Entity';
import { renderColumnWithEllipsis } from '../../utils/renderUtils';

const getHintWithLabel = (label: string, hint: string) => {
  return (
    <Stack direction='horizontal' width='100%' align='space-between' alignY='center'>
      {label}
      {/*<Hint title={hint} size='s' />*/}
    </Stack>
  );
};
export const DependencyOptions = [
  {
    label: getHintWithLabel('Package Hub Dependencies', ''),
    value: DependencyTypes.package
  },
  {
    value: DependencyTypes.custom,
    label: getHintWithLabel('Custom Dependencies', '')
  },
  {
    value: DependencyTypes.system,
    label: getHintWithLabel('Prophecy Provided Dependencies', '')
  }
];

export const PlibDependencyDescription = 'Default dependency for Prophecy Libs';

export const getPlibDependencyName = (type: DependencyTypes) => {
  return type === DependencyTypes.plibPypi ? 'ProphecyLibsPython' : 'ProphecyLibsScala';
};

export const useFilteredData = (
  dependencies: ProjectDependencyType[],
  dependencyType?: DependencyTypes,
  searchStr = ''
) => {
  let filteredData: ProjectDependencyType[] = dependencies;
  if (searchStr) {
    filteredData = filteredData.filter((record) => {
      if (isProphecyLibsDependency(record.type)) {
        return caseInsensitiveInclude(getPlibDependencyName(record.type), searchStr);
      }
      return caseInsensitiveInclude(record.name, searchStr);
    });
  }

  if (dependencyType) {
    if (isCustomDependency(dependencyType)) {
      filteredData = filteredData.filter((record) => isCustomDependency(record.type));
    } else if (isSystemDependency(dependencyType)) {
      filteredData = filteredData.filter((record) => isSystemDependency(record.type));
    } else if (isPackageDependency(dependencyType)) {
      filteredData = filteredData.filter((record) => isPackageDependency(record.type));
    }
  }

  return filteredData;
};

export const isPackageDependency = (type: DependencyTypes) => {
  return type === DependencyTypes.package;
};

export const isProphecyLibsDependency = (type: DependencyTypes) => {
  return type === DependencyTypes.plibMaven || type === DependencyTypes.plibPypi;
};

export const isSystemDependency = (type: DependencyTypes) => {
  return type === DependencyTypes.system || isProphecyLibsDependency(type);
};

export const isCustomDependency = (type: DependencyTypes) => {
  return type === DependencyTypes.maven || type === DependencyTypes.pypi || type === DependencyTypes.custom;
};

export const getDependencyTypeText = (type: DependencyTypes) => {
  let value = `${type}`;
  if (isSystemDependency(type)) {
    value = `Prophecy Provided Dependency`;
  }
  return value;
};

export const SystemEntityName = `System`;

export const getDependencyIcon = (
  type: DependencyTypes,
  entityName?: string,
  isEnabled?: boolean,
  isPipelineGem?: boolean
) => {
  const entityNameText = entityName ? `for ${entityName}` : '';
  let tooltipText = `User Defined Dependency ${entityNameText}`;
  let iconEntityType: EntityIconType = Entity.User;
  if (isSystemDependency(type)) {
    iconEntityType = SystemEntityName;
    tooltipText = `Prophecy Provided Dependency ${entityNameText}`;
  } else if (isPackageDependency(type)) {
    iconEntityType = Entity.Project;
    tooltipText = `Package Hub Dependency ${entityNameText}`;
  }
  return (
    <Tooltip title={tooltipText}>
      <Stack>
        <EntityIcon
          variant={isEnabled ? 'singleColor' : 'gray'}
          size='xs'
          entity={isPipelineGem ? Entity.Gem : iconEntityType}
        />
      </Stack>
    </Tooltip>
  );
};

const getMavenDependencyVersion = (name = '') => {
  const [, , version] = name.split(':');
  return version || '--';
};

const pythonOperatorMatchRegex = /[*~!><=\\]/;
const getPypiDependencyVersion = (name = '') => {
  let operator = '';
  let match = pythonOperatorMatchRegex.exec(name);
  if (match?.[0]) {
    operator = match?.[0];
  }

  let version = '--';
  if (operator) {
    const [, v, v1] = name.split(operator);
    version = operator + v;
    if (v1) {
      version = operator + version + v1;
    }
  }
  return version;
};

export const getVersionColumn = (isResolve?: boolean) => {
  const versionColumn: ColumnType<ProjectDependencyType | DependencyType> = {
    title: 'Version',
    dataIndex: 'version',
    key: 'version',
    width: '90px',
    sortable: true,
    render: (v, record) => {
      const type = record.type;
      const isCustom = isCustomDependency(record.type);
      if (isCustom) {
        const version =
          type === DependencyTypes.pypi
            ? getPypiDependencyVersion(record.package)
            : getMavenDependencyVersion(record.coordinates);
        return renderColumnWithEllipsis(version || '--');
      }
      return renderColumnWithEllipsis(v);
    }
  };

  if (isResolve) {
    versionColumn.onCell = (record) => {
      const props: React.TdHTMLAttributes<HTMLTableCellElement> = {
        style: {
          background: theme.colors.gray50,
          color: record.enabled ? 'inherit' : theme.colors.gray400
        }
      };
      return props;
    };
  }

  return versionColumn;
};
